import { useState, type ReactNode } from 'react';
import { Mail, MessageSquare } from 'lucide-react';
import { Modal } from '../../Modal';
import { Button, Input, SelectionIndicator, Typography } from '../ui';
import { WhatsAppOutlineGlyph } from '../connectors/BrandGlyphs';
import { addContactChannel } from '../../../lib/contact-book';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.module.css';

type ChannelKind = 'whatsapp' | 'sms' | 'email';

// The channel-row icons are all 16 (DS Symphony Button leading-glyph size, Figma 17937:269288) so the
// labels line up. `WhatsAppOutlineGlyph` crops its own viewBox so the mark fills the 16 box like the
// lucide glyphs do (its raw asset has ~30% padding).
const CHANNELS: { kind: ChannelKind; icon: ReactNode }[] = [
  { kind: 'whatsapp', icon: <WhatsAppOutlineGlyph size={16} /> },
  { kind: 'sms', icon: <MessageSquare size={16} aria-hidden /> },
  { kind: 'email', icon: <Mail size={16} aria-hidden /> },
];

/**
 * The "+ Add" channel flow — a two-step dialog (Figma web `17937:269288` select / `17937:269418` form).
 *
 * Step 1 picks a channel type (WhatsApp / SMS / Email); step 2 collects its value and whether it becomes
 * the contact's preferred channel. WhatsApp and SMS are both phone numbers on the People model — only the
 * preferred flag (`communicationChannel`) records which one. There is no Name field: the People model has
 * nowhere per-channel to store one. Native draws the same two steps as a bottom sheet.
 */
export function AddChannelDialog({ personId, open, onClose }: { personId: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const narrow = useNarrow();
  const [kind, setKind] = useState<ChannelKind | null>(null);
  const [value, setValue] = useState('');
  const [preferred, setPreferred] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const channelLabel = (k: ChannelKind) => t(`contacts.channel.${k}`);

  const close = () => {
    setKind(null);
    setValue('');
    setPreferred(false);
    setBusy(false);
    setFailed(false);
    onClose();
  };

  const submit = async () => {
    if (!kind) return;
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setFailed(false);
    logPageClickBi({ page_name: 'contact_detail', button_name: 'save_channel' });
    const ok = await addContactChannel(personId, { channel: kind, value: trimmed, preferred });
    setBusy(false);
    if (!ok) {
      // Kept open with the typed value intact — a failed save that clears the field makes the person retype.
      setFailed(true);
      return;
    }
    close();
  };

  const title = kind ? t('contacts.detail.addChannel.form.title', { channel: channelLabel(kind) }) : t('contacts.detail.addChannel.title');

  return (
    <Modal open={open} onClose={close} title={title} size="minimum" maxHeightPx={650} scrollBody presentation={narrow ? 'sheet' : 'centered'}>
      {kind === null ? (
        <div className={styles.addChannelSelect}>
          {CHANNELS.map(({ kind: k, icon }) => (
            <Button
              key={k}
              variant="text"
              fullWidth
              className={styles.addChannelOption}
              startIcon={icon}
              onClick={() => {
                logPageClickBi({ page_name: 'contact_detail', button_name: `add_channel_${k}` });
                setKind(k);
              }}
            >
              {channelLabel(k)}
            </Button>
          ))}
        </div>
      ) : (
        <div className={styles.addChannelForm}>
          <Input
            label={channelLabel(kind)}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            type={kind === 'email' ? 'email' : 'tel'}
            placeholder={t(kind === 'email' ? 'contacts.detail.addChannel.emailPlaceholder' : 'contacts.detail.addChannel.phonePlaceholder')}
            status={failed ? 'error' : 'default'}
            statusMessage={failed ? t('contacts.detail.add.failed') : undefined}
            autoFocus
          />
          {/* A <label> + visually-hidden checkbox drives the DS circular SelectionIndicator — accessible
              and not a hand-rolled control. Figma draws a radio-shaped mark for "Mark as preferred". */}
          <label className={styles.preferredToggle}>
            <input
              type="checkbox"
              className={styles.preferredCheckbox}
              checked={preferred}
              onChange={(event) => setPreferred(event.target.checked)}
            />
            <SelectionIndicator shape="radio" checked={preferred} />
            <Typography variant="p1" color="primary">
              {t('contacts.detail.addChannel.preferred')}
            </Typography>
          </label>
          <Button fullWidth loading={busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      )}
    </Modal>
  );
}
